import { DeepPartial, Raw } from '@/modules/common/helpers/api';
import { RawSecurity, Security } from '@/modules/common/models';
import {
  OrderIntent,
  OrderSide,
  OrderType,
  TimeInForceType,
} from '@/modules/marketplace/types/marketplace';
import Decimal from 'decimal.js';
import { parseISO } from 'date-fns';
import { RateType } from '@/connect';

type RawOrderBasketParams = Raw<
  OrderBasketParams,
  {
    // always specify existing raw entry types explicitly
    entries: RawOmsBasketItem[];
    agreementIds: string[];
  }
>;

export abstract class OrderBasketParams {
  public companyId: string;
  public userId: string;
  public side: OrderSide;
  public entries: OmsBasketItem[];
  public agreementIds: string[];
  public allowOddLots: boolean;

  protected constructor(data: RawOrderBasketParams) {
    this.companyId = data.companyId;
    this.userId = data.userId;
    this.side = data.side;
    this.agreementIds = data.agreementIds;
    this.entries = data.entries.map<OmsBasketItem>((item) =>
      OmsBasketItem.fromData(item, this.agreementIds)
    );
    this.allowOddLots = data.allowOddLots;
  }

  public static toData(model: OrderBasketParams): RawOrderBasketParams {
    return {
      companyId: model.companyId,
      userId: model.userId,
      side: model.side,
      entries: model.entries.map((e) => OmsBasketItem.toData(e, model.agreementIds)),
      agreementIds: model.agreementIds,
      allowOddLots: model.allowOddLots,
    };
  }
}

export type RawOmsBasketItem = Raw<
  OmsBasketItem,
  {
    // always specify existing raw entry types explititly
    equity: RawSecurity;
    agreementIndexes: number[];
    rateType: RateType | keyof typeof RateType;
  },
  'security' | 'agreementIds'
>;

export class OmsBasketItem {
  public security: Security;
  public side: OrderSide; // required by backend when we post back the basket, but not used in the UI
  public quantity: number;
  public type: OrderType;
  public intent: OrderIntent;
  public rate: Decimal | null;
  public rateType: RateType;
  public active: boolean;
  public timeInForceType: TimeInForceType;
  public goodTillTime: Date | null;
  public agreementIds: string[];
  public minQuantity: number;
  public row: number;

  protected constructor(data: RawOmsBasketItem, agreementIds: string[]) {
    this.security = Security.fromData(data.equity);
    this.side = data.side;
    this.quantity = data.quantity;
    this.type = data.type;
    this.intent = data.intent;
    this.rate = data.rate !== null ? new Decimal(data.rate) : null;
    this.rateType = RateType[data.rateType as keyof typeof RateType];

    this.active = data.active;
    this.timeInForceType = data.timeInForceType;
    this.goodTillTime = data.goodTillTime !== null ? parseISO(data.goodTillTime) : null;
    this.agreementIds = data.agreementIndexes.map<string>((idx) => agreementIds[idx]);
    this.minQuantity = data.minQuantity;
    this.row = data.row;
  }

  public static fromData(data: RawOmsBasketItem, agreementIds: string[]): OmsBasketItem {
    return new OmsBasketItem(data, agreementIds);
  }

  public static toData(model: OmsBasketItem, agreementIds: string[]): RawOmsBasketItem {
    return {
      equity: Security.toData(model.security),
      side: model.side,
      quantity: model.quantity,
      type: model.type,
      intent: model.intent,
      rate: model.rate !== null ? model.rate.toString() : null,
      rateType: RateType[model.rateType] as keyof typeof RateType,
      active: model.active,
      timeInForceType: model.timeInForceType,
      goodTillTime: model.goodTillTime !== null ? model.goodTillTime.toISOString() : null,
      agreementIndexes: model.agreementIds.map<number>((agreementId) =>
        agreementIds.findIndex((a) => a === agreementId)
      ),
      minQuantity: model.minQuantity,
      row: model.row,
    };
  }

  public static mock(data?: DeepPartial<RawOmsBasketItem>, agreementIds?: string[]): OmsBasketItem {
    return OmsBasketItem.fromData(OmsBasketItem.mockData(data), agreementIds ?? []);
  }

  public static mockData(data?: DeepPartial<RawOmsBasketItem>): RawOmsBasketItem {
    const { equity, agreementIndexes, ...rest } = data ?? {};

    return {
      equity: Security.mockData(equity),
      side: 'BORROW',
      active: true,
      goodTillTime: null,
      intent: 'FIRM',
      minQuantity: 10,
      quantity: 10,
      rate: null,
      rateType: RateType.FEE,
      row: 1,
      timeInForceType: 'DAY',
      type: 'LIMIT',
      agreementIndexes: agreementIndexes ?? [],
      ...rest,
    };
  }
}
